import { useState, useEffect } from 'react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const IOS_GUIDE_SHOWN_KEY = 'pwa-ios-guide-shown';
const IOS_REMINDER_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<Event & { prompt: () => void; userChoice: Promise<{ outcome: string }> } | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches
  );
  const [isDismissed, setIsDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isJustInstalled, setIsJustInstalled] = useState(false);
  const [showIOSReminder, setShowIOSReminder] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    setIsIOS(
      /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
      !(window as Window & { MSStream?: unknown }).MSStream
    );

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < DISMISS_DURATION_MS) {
      setIsDismissed(true);
    }

    // iOS 리마인더 체크: 가이드 확인 후 30일 내, standalone 아닐 때
    const iosGuideShownAt = localStorage.getItem(IOS_GUIDE_SHOWN_KEY);
    if (iosGuideShownAt) {
      const elapsed = Date.now() - parseInt(iosGuideShownAt);
      if (elapsed < IOS_REMINDER_DURATION_MS) {
        setShowIOSReminder(true);
      } else {
        localStorage.removeItem(IOS_GUIDE_SHOWN_KEY);
      }
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as Event & { prompt: () => void; userChoice: Promise<{ outcome: string }> });
      setIsInstallable(true);
    };

    const installedHandler = () => {
      setIsJustInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
  };

  const dismissIOSGuide = () => {
    localStorage.setItem(IOS_GUIDE_SHOWN_KEY, String(Date.now()));
    setIsDismissed(true);
    setShowIOSReminder(true);
  };

  const dismissIOSReminder = () => {
    setShowIOSReminder(false);
  };

  const reshowIOSGuide = () => {
    localStorage.removeItem(IOS_GUIDE_SHOWN_KEY);
    setShowIOSReminder(false);
    setIsDismissed(false);
  };

  const showIOSGuide = isIOS && !isInstalled && !isDismissed && !showIOSReminder;
  const showBanner = (isInstallable && !isInstalled && !isDismissed) || showIOSGuide || showIOSReminder;

  return {
    showBanner,
    showIOSGuide,
    showIOSReminder,
    install,
    dismiss,
    dismissIOSGuide,
    dismissIOSReminder,
    reshowIOSGuide,
    isInstalled,
    isJustInstalled,
  };
}
